import React from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import type { QuoteItem } from '@techgitter/contracts';
import {
  assignItemToGroup,
  autoIncludesText,
  groupLineName,
  groupLineTotal,
  newBillingGroupId,
  renameBillingGroup,
  resolveBillingGroups,
  setBillingGroupDescription,
  ungroupBillingGroup,
  type BillingGroup,
} from '../../utils/quoteBillingGroups';

const NEW_GROUP = '__new__';

const fmtCurrency = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

/**
 * Per-item "Bills on" selector. Lets the tech put this item on its own line
 * (default), into an existing billing group, or into a brand-new group.
 * Grouped items collapse into one summed customer line — managed in the
 * {@link BillingGroupsPanel} below the items.
 */
export const BillingGroupSelect: React.FC<{
  item: QuoteItem;
  items: QuoteItem[];
  onChange: (items: QuoteItem[]) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  sx?: React.ComponentProps<typeof TextField>['sx'];
}> = ({ item, items, onChange, disabled, size = 'small', sx }) => {
  const groups = resolveBillingGroups(items);
  const current = item.billingGroupId ?? '';
  const value = groups.some((g) => g.id === current) ? current : '';
  return (
    <TextField
      select
      size={size}
      label="Bills on"
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === NEW_GROUP) {
          onChange(assignItemToGroup(items, item.id, newBillingGroupId()));
        } else {
          onChange(assignItemToGroup(items, item.id, v === '' ? null : v));
        }
      }}
      disabled={disabled}
      sx={sx ?? { minWidth: 160 }}
    >
      <MenuItem value="">Own line</MenuItem>
      {groups.map((g) => (
        <MenuItem key={g.id} value={g.id}>
          {groupLineName(g)}
        </MenuItem>
      ))}
      <MenuItem value={NEW_GROUP}>＋ New billing group…</MenuItem>
    </TextField>
  );
};

/**
 * Summary panel listing each billing group with an editable customer-facing
 * line name + description (placeholder = the auto "Includes: …"), its member
 * count and summed price, and an Ungroup action. Renders nothing when there
 * are no groups. `priceDisplayMode` controls the summed-price GST mode; the
 * shown total is the pre-EFT sum (the EFT buffer, if on, is applied at the
 * quote total / preview / invoice, not per group here).
 */
export const BillingGroupsPanel: React.FC<{
  items: QuoteItem[];
  onChange: (items: QuoteItem[]) => void;
  disabled?: boolean;
  priceDisplayMode?: 'ex' | 'inc';
}> = ({ items, onChange, disabled, priceDisplayMode = 'ex' }) => {
  const groups = resolveBillingGroups(items);
  if (groups.length === 0) return null;
  const gstTag = priceDisplayMode === 'inc' ? 'inc GST' : 'ex GST';

  const renderGroup = (g: BillingGroup) => (
    <Paper key={g.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          label="Invoice line code"
          size="small"
          value={g.rawName}
          placeholder="e.g. HW.PC.Desktop.Custom"
          helperText="Invoice product/SKU — not shown to the customer"
          onChange={(e) => onChange(renameBillingGroup(items, g.id, e.target.value))}
          disabled={disabled}
          sx={{ width: 240 }}
        />
        <TextField
          label="Description"
          size="small"
          value={g.description ?? ''}
          placeholder={autoIncludesText(g.items) || 'Line description (what the customer sees)'}
          onChange={(e) =>
            onChange(
              setBillingGroupDescription(
                items,
                g.id,
                e.target.value === '' ? null : e.target.value,
              ),
            )
          }
          disabled={disabled}
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
        />
        <Tooltip title="Ungroup — each item bills on its own line again">
          <span>
            <IconButton
              size="small"
              onClick={() => onChange(ungroupBillingGroup(items, g.id))}
              disabled={disabled}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {g.items.length} item{g.items.length === 1 ? '' : 's'} · one line at{' '}
        <strong>{fmtCurrency(groupLineTotal(g, priceDisplayMode))}</strong> {gstTag}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
        Billing groups
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Grouped items bill as a single line (summed price); their individual
        prices aren't shown to the customer. The <strong>code</strong> is the
        invoice product/SKU (internal); the customer sees the{' '}
        <strong>description</strong>.
      </Typography>
      {groups.map(renderGroup)}
    </Box>
  );
};
